import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Ship, MapPin, Phone, Mail } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const ShippingAgent = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  // ── State ──────────────────────────────────────────────────────────────────
  const [agents, setAgents]                 = useState([]);
  const [countries, setCountries]           = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  const [showModal, setShowModal]           = useState(false);
  const [editMode, setEditMode]             = useState(false);
  const [submitLoading, setSubmitLoading]   = useState(false);

  const emptyForm = {
    ShippingAgentID: null,
    AgentName:       '',
    ReferenceCode:   '',
    ContactPerson:   '',
    Address1:        '',
    Address2:        '',
    Address3:        '',
    Phone:           '',
    Fax:             '',
    Email:           '',
    WebSite:         '',
    StatusActive:    true,
    Remarks:         '',
    CountryID:       ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors]     = useState({});

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchCountries();
    fetchAgents('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    if (companyId && yearId) fetchAgents(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    filterAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, agents]);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php');
      if (res.data.success) setCountries(res.data.data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const fetchAgents = async (status = 'all') => {
    setLoading(true);
    try {
      const res = await apiClient.get('/ShippingAgent.php', { params: { status } });
      if (res.data.success) {
        setAgents(res.data.data || []);
      } else {
        alert('Failed to fetch shipping agents: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error fetching shipping agents:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching shipping agents.');
    } finally {
      setLoading(false);
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filterAgents = () => {
    if (!searchTerm.trim()) { setFilteredAgents(agents); return; }
    const q = searchTerm.toLowerCase();
    setFilteredAgents(
      agents.filter(a =>
        a.AgentName.toLowerCase().includes(q) ||
        a.ReferenceCode?.toLowerCase().includes(q) ||
        a.ContactPerson?.toLowerCase().includes(q)
      )
    );
  };

  // ── Status badge (Active only) ─────────────────────────────────────────────
  const getStatusBadge = (agent) => {
    return Number(agent.StatusActive) === 1
      ? <span className="status-badge active">Active</span>
      : <span className="status-badge inactive">Inactive</span>;
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.AgentName.trim()) errs.AgentName = 'Agent name is required';
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email))
      errs.Email = 'Invalid email format';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        StatusActive: formData.StatusActive ? 1 : 0,
        CountryID:    formData.CountryID || null
      };

      const method = editMode ? 'put' : 'post';
      const res    = await apiClient[method]('/ShippingAgent.php', payload);

      if (res.data.success) {
        alert(editMode ? 'Shipping agent updated successfully!' : 'Shipping agent created successfully!');
        setShowModal(false);
        resetForm();
        fetchAgents(statusFilter);
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error saving:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = async (agent) => {
    try {
      const res = await apiClient.get(`/ShippingAgent.php?id=${agent.ShippingAgentID}`);
      if (res.data.success) {
        const d = res.data.data;
        setFormData({
          ShippingAgentID: d.ShippingAgentID,
          AgentName:       d.AgentName     || '',
          ReferenceCode:   d.ReferenceCode || '',
          ContactPerson:   d.ContactPerson || '',
          Address1:        d.Address1      || '',
          Address2:        d.Address2      || '',
          Address3:        d.Address3      || '',
          Phone:           d.Phone         || '',
          Fax:             d.Fax           || '',
          Email:           d.Email         || '',
          WebSite:         d.WebSite       || '',
          Remarks:         d.Remarks       || '',
          CountryID:       d.CountryID     || '',
          StatusActive:    Number(d.StatusActive) === 1,
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (err) {
      console.error('Error loading agent:', err);
      alert('Error loading shipping agent details');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shipping agent?')) return;
    try {
      const res = await apiClient.delete('/ShippingAgent.php', { data: { ShippingAgentID: id } });
      if (res.data.success) {
        alert('Shipping agent deleted successfully!');
        fetchAgents(statusFilter);
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting: ' + (err.response?.data?.message || 'Please try again.'));
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resetForm    = () => { setFormData(emptyForm); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="master-container">

      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Shipping Agent Master</h2>
          <p className="subtitle">Manage shipping agents and carriers</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} /> Add Shipping Agent
        </button>
      </div>

      {/* Toolbar */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by agent name, code, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="toolbar-info">
          Total: {filteredAgents.length} {filteredAgents.length === 1 ? 'agent' : 'agents'}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading shipping agents...</div>
      ) : filteredAgents.length === 0 ? (
        <div className="empty-state">
          <p>No shipping agents found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Reference Code</th>
                <th>Agent Name</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Country</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => (
                <tr key={agent.ShippingAgentID}>
                  <td className="font-mono">{agent.ReferenceCode || '—'}</td>
                  <td className="font-semibold">{agent.AgentName}</td>
                  <td>{agent.ContactPerson || '—'}</td>
                  <td><div className="contact-item"><Phone size={14} />{agent.Phone || '—'}</div></td>
                  <td><div className="contact-item"><Mail  size={14} />{agent.Email || '—'}</div></td>
                  <td>{agent.countryname || '—'}</td>
                  <td>{getStatusBadge(agent)}</td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit"   onClick={() => handleEdit(agent)}                   title="Edit">   <Edit2  size={16} /></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(agent.ShippingAgentID)} title="Delete"> <Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Shipping Agent' : 'Add New Shipping Agent'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">

              {/* Basic Info */}
              <div className="form-section">
                <h4><Ship size={18} /> Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="AgentName">Agent Name <span className="required">*</span></label>
                    <input type="text" id="AgentName" autoFocus
                      value={formData.AgentName}
                      onChange={(e) => setFormData({ ...formData, AgentName: e.target.value })}
                      maxLength="100" placeholder="e.g., Maersk Line"
                      className={errors.AgentName ? 'error' : ''} />
                    {errors.AgentName && <span className="error-message">{errors.AgentName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="ReferenceCode">Reference Code</label>
                    <input type="text" id="ReferenceCode"
                      value={formData.ReferenceCode}
                      onChange={(e) => setFormData({ ...formData, ReferenceCode: e.target.value.toUpperCase() })}
                      maxLength="50" placeholder="e.g., MAERSK" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ContactPerson">Contact Person</label>
                    <input type="text" id="ContactPerson"
                      value={formData.ContactPerson}
                      onChange={(e) => setFormData({ ...formData, ContactPerson: e.target.value })}
                      maxLength="100" placeholder="e.g., John Smith" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="CountryID">Country</label>
                    <select id="CountryID" value={formData.CountryID}
                      onChange={(e) => setFormData({ ...formData, CountryID: e.target.value })}>
                      <option value="">— Select Country —</option>
                      {countries.map((c) => (
                        <option key={c.countryid} value={c.countryid}>{c.countryname}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="form-section">
                <h4><MapPin size={18} /> Address Information</h4>
                <div className="form-group">
                  <label htmlFor="Address1">Address Line 1</label>
                  <input type="text" id="Address1" value={formData.Address1}
                    onChange={(e) => setFormData({ ...formData, Address1: e.target.value })}
                    maxLength="100" placeholder="Street address" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Address2">Address Line 2</label>
                    <input type="text" id="Address2" value={formData.Address2}
                      onChange={(e) => setFormData({ ...formData, Address2: e.target.value })}
                      maxLength="100" placeholder="Apartment, suite" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Address3">Address Line 3</label>
                    <input type="text" id="Address3" value={formData.Address3}
                      onChange={(e) => setFormData({ ...formData, Address3: e.target.value })}
                      maxLength="100" placeholder="Additional info" />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="form-section">
                <h4><Phone size={18} /> Contact Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Phone">Phone</label>
                    <input type="text" id="Phone" value={formData.Phone}
                      onChange={(e) => setFormData({ ...formData, Phone: e.target.value })}
                      maxLength="50" placeholder="+971-4-1234567" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Fax">Fax</label>
                    <input type="text" id="Fax" value={formData.Fax}
                      onChange={(e) => setFormData({ ...formData, Fax: e.target.value })}
                      maxLength="50" placeholder="+971-4-7654321" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Email">Email</label>
                    <input type="email" id="Email" value={formData.Email}
                      onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                      maxLength="100" placeholder="info@agent.com"
                      className={errors.Email ? 'error' : ''} />
                    {errors.Email && <span className="error-message">{errors.Email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="WebSite">Website</label>
                    <input type="text" id="WebSite" value={formData.WebSite}
                      onChange={(e) => setFormData({ ...formData, WebSite: e.target.value })}
                      maxLength="100" placeholder="www.agent.com" />
                  </div>
                </div>
              </div>

              {/* Status & Remarks */}
              <div className="form-section">
                <h4>Status &amp; Remarks</h4>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', width: 'fit-content' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.StatusActive}
                    onChange={(e) => setFormData({ ...formData, StatusActive: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Active
                </label>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="Remarks">Remarks</label>
                  <textarea id="Remarks" rows="3"
                    value={formData.Remarks}
                    onChange={(e) => setFormData({ ...formData, Remarks: e.target.value })}
                    maxLength="200" placeholder="Additional notes or comments..." />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingAgent;